import { ROUTES } from '@/constants/app';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAnnonceAssistant } from '../hooks/useAnnonceAssistant';

/**
 * Ce que l'assistant sait faire, par famille. Noms fictifs seulement : cette
 * page est lue par tous les cabinets.
 */
const FAMILLES: { titre: string; exemples: string[] }[] = [
  {
    titre: 'Consulter',
    exemples: [
      'Quels dossiers n’ont pas leur DSN d’août ?',
      'Où en est la DSN de Cabinet Exemple pour juillet ?',
      'Liste les salariés d’Ateliers Voltaire',
      'Les bulletins d’août d’Ateliers Voltaire',
      'Qu’est-ce qui est parti par email pour Cabinet Exemple ce mois-ci ?',
    ],
  },
  {
    titre: 'Lancer une automatisation déjà paramétrée',
    exemples: [
      'Lance les bulletins de Cabinet Exemple',
      'Envoie les bulletins d’août d’Ateliers Voltaire',
    ],
  },
  {
    titre: 'Produire une édition',
    exemples: [
      'Le journal de paie d’août pour Ateliers Voltaire',
      'Quelles éditions sont disponibles pour Cabinet Exemple ?',
    ],
  },
  {
    titre: 'Coffres-forts',
    exemples: [
      'Dépose les bulletins d’août dans les coffres d’Ateliers Voltaire',
      'Renvoie l’invitation au coffre à un salarié',
    ],
  },
  {
    titre: 'Coordonnées d’un salarié',
    exemples: ['Change l’adresse d’un salarié d’Ateliers Voltaire'],
  },
];

/**
 * La documentation de l'assistant — dans l'application, à la charte des
 * autres écrans. Pas de mode d'emploi en tête : les exemples parlent, et les
 * limites sont dites clairement.
 */
export function AideAssistantPage() {
  // Cette page ne s'ouvre qu'à un compte qui a l'assistant (une entreprise
  // cliente est redirigée avant, navigation-client.ts) : la question au
  // serveur est celle déjà posée par l'application, servie depuis le cache.
  const { aValider } = useAnnonceAssistant({ actif: true });
  return (
    <div className="mx-auto max-w-3xl">
      {/* Ouverte depuis l'annonce, avant de valider : on y retourne d'un
          clic — l'écran se réaffiche sur n'importe quelle autre adresse. */}
      {aValider && (
        <Link
          to={ROUTES.HOME}
          className="mb-3 inline-flex min-h-8 items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-800 hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          Retour à l’annonce
        </Link>
      )}
      <div className="flex items-center gap-3">
        <span className="bg-brand-50 text-brand-700 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
          <Sparkles className="h-5 w-5" />
        </span>
        <h1 className="text-xl font-bold text-neutral-900">
          L’assistant — ce qu’on peut lui demander
        </h1>
      </div>
      <p className="mt-2 text-sm leading-6 text-neutral-600">
        Écrivez votre demande en français, comme vous la diriez à un collègue.
        L’assistant répond, ou vous propose une action et attend votre
        confirmation avant de la lancer.{' '}
        <Link
          to={ROUTES.ASSISTANT}
          className="text-primary-600 hover:text-primary-700 font-medium hover:underline"
        >
          Ouvrir l’assistant →
        </Link>
      </p>

      <div className="mt-6 space-y-4">
        {FAMILLES.map(famille => (
          <section
            key={famille.titre}
            aria-labelledby={`aide-${famille.titre}`}
            className="rounded-xl border border-neutral-200 bg-white p-4"
          >
            <h2
              id={`aide-${famille.titre}`}
              className="text-sm font-semibold text-neutral-900"
            >
              {famille.titre}
            </h2>
            <ul className="mt-3 flex flex-wrap gap-2">
              {famille.exemples.map(exemple => (
                <li
                  key={exemple}
                  className="min-h-9 rounded-full border border-neutral-200 bg-gray-50 px-3 py-2 text-sm leading-5 text-neutral-700"
                >
                  {exemple}
                </li>
              ))}
            </ul>
          </section>
        ))}

        <section
          aria-labelledby="aide-limites"
          className="border-warning-200 bg-warning-50 rounded-xl border p-4"
        >
          <h2
            id="aide-limites"
            className="text-warning-900 text-sm font-semibold"
          >
            Ce que l’assistant ne fait pas
          </h2>
          <ul className="text-warning-900 mt-2 list-disc space-y-1 pl-5 text-sm leading-6">
            <li>
              Il ne valide pas un bulletin : il les calcule ou les envoie quand
              l’automatisation existe, la validation reste dans OpenPaye.
            </li>
            <li>
              Il n’envoie pas une DSN : il dit où elle en est, et lance le
              calcul si une automatisation DSN est paramétrée sur le dossier.
            </li>
            <li>
              Il ne crée ni dossier, ni salarié, ni contrat, et ne modifie
              aucun montant.
            </li>
          </ul>
        </section>

        <section
          aria-labelledby="aide-contexte"
          className="rounded-xl border border-neutral-200 bg-white p-4"
        >
          <h2
            id="aide-contexte"
            className="text-sm font-semibold text-neutral-900"
          >
            Le dossier en contexte
          </h2>
          <p className="mt-2 text-sm leading-6 text-neutral-700">
            Ouvert depuis la page d’un dossier, l’assistant le garde en
            contexte : une pastille avec son nom apparaît en haut de l’écran,
            et vous n’avez plus à le répéter. Pour en changer, cliquez sur la
            croix de la pastille, ou ouvrez l’assistant depuis un autre
            dossier : la conversation repart à zéro, sur le nouveau dossier.
          </p>
        </section>

        <section
          aria-labelledby="aide-donnees"
          className="border-primary-200 bg-primary-50 rounded-xl border p-4"
        >
          <h2
            id="aide-donnees"
            className="text-primary-900 text-sm font-semibold"
          >
            Vos données
          </h2>
          <ul className="text-primary-900 mt-2 space-y-1 text-sm leading-6">
            <li>
              Votre demande est traitée par Google Cloud dans l’Union
              européenne (Belgique). Elle n’est jamais utilisée pour entraîner
              un modèle.
            </li>
            <li>
              Seule votre demande (le texte que vous écrivez et le nom du
              dossier ouvert) est envoyée à Google Cloud. Vos bulletins,
              montants et listes de salariés ne lui sont jamais transmis :
              c’est Innov’Paie qui les lit, sous vos droits.
            </li>
            <li>
              Le texte de vos demandes est effacé au bout de trente jours ; la
              trace (date, action choisie) est gardée douze mois.
            </li>
          </ul>
          <Link
            to={ROUTES.LEGAL.PRIVACY}
            className="text-primary-700 hover:text-primary-800 mt-2 inline-block text-sm font-medium hover:underline"
          >
            Politique de confidentialité →
          </Link>
        </section>

        <section
          aria-labelledby="aide-limite-quotidienne"
          className="rounded-xl border border-neutral-200 bg-white p-4"
        >
          <h2
            id="aide-limite-quotidienne"
            className="text-sm font-semibold text-neutral-900"
          >
            Limites
          </h2>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-6 text-neutral-700">
            <li>Une demande fait 2 000 caractères au plus.</li>
            <li>
              Un plafond de demandes par jour et par personne s’applique ;
              l’assistant vous le dit s’il est atteint, et les écrans restent
              disponibles.
            </li>
            <li>
              Une action lancée peut durer plusieurs minutes (calcul de
              bulletins, édition) : son état s’affiche dans la conversation et
              dans l’historique du dossier.
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}
